import { supabase } from '../supabaseClient';
import { getAdminStatsThrottled } from '../../utils/requestThrottler';

export class AuditService {
  static async recordAuditLog(eventType, {
    entity,
    entityId,
    summary,
    metadata,
    performedBy
  } = {}) {
    try {
      const payload = {
        event_type: eventType,
        entity,
        entity_id: entityId,
        summary,
        metadata: metadata || {},
        performed_by: performedBy || null
      };

      const { error } = await supabase
        .from('audit_logs')
        .insert([payload]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.warn('Audit log write failed:', error?.message || error);
      return false;
    }
  }

  // AUDIT LOGS
  static async getAuditLogs({
    page = 1,
    pageSize = 25,
    eventType,
    entity,
    search,
    dateFrom,
    dateTo,
    sortBy = 'created_at',
    sortDir = 'desc'
  } = {}) {
    try {
      const from = Math.max(0, (page - 1) * pageSize);
      const to = from + pageSize - 1;

      let query = supabase
        .from('audit_logs')
        .select('*', { count: 'exact' });

      if (eventType && eventType !== 'all') {
        query = query.eq('event_type', eventType);
      }
      if (entity && entity !== 'all') {
        query = query.eq('entity', entity);
      }
      if (dateFrom) {
        query = query.gte('created_at', dateFrom);
      }
      if (dateTo) {
        query = query.lte('created_at', dateTo);
      }
      if (search && search.trim()) {
        const term = `%${search.trim()}%`;
        query = query.or(`ilike(summary,${term}),ilike(performed_by,${term}),ilike(entity_id,${term})`);
      }

      const allowedSort = new Set(['created_at', 'event_type', 'entity']);
      const sortField = allowedSort.has(sortBy) ? sortBy : 'created_at';
      query = query.order(sortField, { ascending: String(sortDir).toLowerCase() === 'asc' });

      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;
      return { rows: data || [], count: count || 0 };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return { rows: [], count: 0 };
    }
  }

  static async getAuditLogFilters() {
    try {
      const eventQuery = supabase
        .from('audit_logs')
        .select('event_type', { distinct: true })
        .order('event_type');
      const entityQuery = supabase
        .from('audit_logs')
        .select('entity', { distinct: true })
        .order('entity');

      const [{ data: events, error: eventErr }, { data: entities, error: entityErr }] = await Promise.all([eventQuery, entityQuery]);

      if (eventErr) throw eventErr;
      if (entityErr) throw entityErr;

      const eventTypes = (events || [])
        .map((row) => row.event_type)
        .filter(Boolean);
      const entityTypes = (entities || [])
        .map((row) => row.entity)
        .filter(Boolean);

      return {
        eventTypes,
        entityTypes
      };
    } catch (error) {
      console.error('Error fetching audit log filters:', error);
      return { eventTypes: [], entityTypes: [] };
    }
  }

  static async exportAuditLogsCSV(params = {}) {
    const { rows } = await AuditService.getAuditLogs({
      ...params,
      pageSize: 1000,
      page: 1
    });
    const headers = ['Timestamp', 'Event', 'Entity', 'Entity ID', 'Summary', 'Actor', 'Metadata'];
    const serializeMeta = (meta) => {
      if (!meta) return '';
      try {
        return JSON.stringify(meta);
      } catch (e) {
        return String(meta);
      }
    };
    const csvRows = [headers.join(',')];
    rows.forEach((row) => {
      const line = [
        row.created_at || '',
        row.event_type || '',
        row.entity || '',
        row.entity_id || '',
        row.summary ? row.summary.replace(/\n/g, ' ') : '',
        row.performed_by || ''
      ].map((value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (/[\",\n]/.test(str)) return '"' + str.replace(/"/g, '""') + '"';
        return str;
      });
      line.push(serializeMeta(row.metadata));
      csvRows.push(line.join(','));
    });
    return csvRows.join('\n');
  }

  // STATISTICS AND ANALYTICS
  static async getAdminStats() {
    try {
      console.log('[AdminService] Fetching admin stats via throttled request...');
      const stats = await getAdminStatsThrottled();
      return stats;
    } catch (error) {
      console.warn('[AdminService] Admin stats not available (tables may not exist yet):', error.message);
      return {
        countries: 0,
        pathways: 0,
        translations: 0,
        packages: 0,
        loanOptions: 0,
        wizardSessions: 0,
        wizardSubmissions: 0
      };
    }
  }
}
